import React, { useState, useCallback, useRef } from 'react';

export interface PageSliderProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  chapterTitle?: string;
}

export const PageSlider: React.FC<PageSliderProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  chapterTitle,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPage, setHoverPage] = useState<number | null>(null);
  const sliderRef = useRef<HTMLInputElement>(null);

  const percentage = ((currentPage - 1) / (totalPages - 1)) * 100;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val)) {
        onPageChange(val);
      }
    },
    [onPageChange]
  );

  const handlePointerDown = () => {
    setIsDragging(true);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const target = Math.round(1 + ratio * (totalPages - 1));
    setHoverPage(target);
  };

  const handleMouseLeave = () => {
    setHoverPage(null);
  };

  const activeTooltipPage = isDragging ? currentPage : hoverPage;
  const activeTooltipPercent = activeTooltipPage
    ? ((activeTooltipPage - 1) / (totalPages - 1)) * 100
    : percentage;

  return (
    <div className="page-slider-container">
      {(isDragging || hoverPage !== null) && activeTooltipPage && (
        <div
          className="slider-floating-tooltip"
          style={{ left: `${Math.max(6, Math.min(94, activeTooltipPercent))}%` }}
        >
          <span className="tooltip-page-number">Page {activeTooltipPage}</span>
          {chapterTitle && <span className="tooltip-chapter-title">{chapterTitle}</span>}
        </div>
      )}

      <div className="slider-track-wrap">
        <input
          ref={sliderRef}
          type="range"
          min={1}
          max={totalPages}
          step={1}
          value={currentPage}
          onChange={handleChange}
          onMouseDown={handlePointerDown}
          onMouseUp={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchEnd={handlePointerUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="page-slider-range"
          aria-label="Book page navigation slider"
          aria-valuemin={1}
          aria-valuemax={totalPages}
          aria-valuenow={currentPage}
          aria-valuetext={`Page ${currentPage} of ${totalPages}`}
          style={{
            background: `linear-gradient(to right, var(--theme-accent, #00f3ff) 0%, var(--theme-accent, #00f3ff) ${percentage}%, var(--void-border, #1c2738) ${percentage}%, var(--void-border, #1c2738) 100%)`,
          }}
        />
      </div>
    </div>
  );
};
